import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

export function useAuth() {
  const router = useRouter()
  const authStore = useAuthStore()
  
  // Estado local para el composable
  const isInitialized = ref(false)
  const error = ref(null)
  const success = ref(null)

  // Getters computados del store
  const user = computed(() => authStore.userInfo)
  const isAuthenticated = computed(() => authStore.isAuthenticated)
  const isLoading = computed(() => authStore.loading)
  const tokenExpiry = computed(() => authStore.tokenExpiry)
  const minutesUntilExpiry = computed(() => authStore.minutesUntilExpiry)

  // Funciones de autenticación
  const login = async (credentials) => {
    error.value = null
    success.value = null

    try {
      const result = await authStore.login(credentials)
      
      if (result.success) {
        success.value = 'Inicio de sesión exitoso'
        // Redirigir a la página principal o a la ruta solicitada
        const redirectTo = router.currentRoute.value.query.redirect || '/'
        await router.push(redirectTo)
        return { success: true }
      } else {
        error.value = result.error
        return { success: false, error: result.error }
      }
    } catch (err) {
      error.value = 'Error inesperado al iniciar sesión'
      return { success: false, error: error.value }
    }
  }

  const logout = async () => {
    error.value = null
    success.value = null

    try {
      await authStore.logout()
      success.value = 'Sesión cerrada correctamente'
      await router.push('/login')
      return { success: true }
    } catch (err) {
      error.value = 'Error al cerrar sesión'
      return { success: false, error: error.value }
    }
  }

  const checkAuth = async () => {
    if (!isInitialized.value) {
      try {
        const isValid = await authStore.initializeAuth()
        isInitialized.value = true
        return isValid
      } catch (err) {
        console.error('Error al inicializar autenticación:', err)
        isInitialized.value = true
        return false
      }
    }
    return authStore.isAuthenticated
  }

  const refreshToken = async () => {
    try {
      const success = await authStore.refreshToken()
      return success
    } catch (err) {
      console.error('Error al renovar token:', err)
      return false
    }
  }

  const getCurrentUser = async () => {
    try {
      const user = await authStore.getCurrentUser()
      return user
    } catch (err) {
      console.error('Error al obtener usuario actual:', err)
      return null
    }
  }

  // Funciones de utilidad
  const clearMessages = () => {
    error.value = null
    success.value = null
  }

  const requireAuth = async (redirectTo = '/login') => {
    const isAuth = await checkAuth()
    if (!isAuth) {
      await router.push(redirectTo)
      return false
    }
    return true
  }

  const redirectIfAuthenticated = async (redirectTo = '/') => {
    const isAuth = await checkAuth()
    if (isAuth) {
      await router.push(redirectTo)
      return true
    }
    return false
  }

  // Inicialización automática
  onMounted(async () => {
    if (!isInitialized.value) {
      await checkAuth()
    }
  })

  // Limpieza al desmontar
  onUnmounted(() => {
    clearMessages()
  })

  return {
    // Estado
    user,
    isAuthenticated,
    isLoading,
    isInitialized,
    error,
    success,
    tokenExpiry,
    minutesUntilExpiry,

    // Funciones
    login,
    logout,
    checkAuth,
    refreshToken,
    getCurrentUser,
    clearMessages,
    requireAuth,
    redirectIfAuthenticated
  }
}
